/**
 * Shared logging utility that sends errors to Azure Application Insights
 * when available (production) and always logs to console in development.
 *
 * Usage:
 *   import { logError, logWarning, logTrace } from '~shared/util/logUtil'
 *   logError('CatalogStore.fetchMenu', error, { itemId: '123' })
 */

type LogProperties = Record<string, string | number | boolean | undefined>

function isDev(): boolean {
  return process.env.NODE_ENV !== 'production'
}

function getAppInsights() {
  return typeof window !== 'undefined' ? (window as any).appInsights : undefined
}

export function logError(source: string, error: unknown, properties?: LogProperties): void {
  const err = error instanceof Error ? error : new Error(String(error))

  if (isDev()) {
    console.error(`[${source}]`, err.message, properties ?? '')
  }

  getAppInsights()?.trackException({
    exception: err,
    properties: {
      source,
      ...properties,
    },
  })
}

export function logWarning(source: string, message: string, properties?: LogProperties): void {
  if (isDev()) {
    console.warn(`[${source}]`, message, properties ?? '')
  }

  getAppInsights()?.trackTrace({
    message: `[${source}] ${message}`,
    severityLevel: 2, // Warning
    properties,
  })
}

export function logTrace(source: string, message: string, properties?: LogProperties): void {
  if (isDev()) {
    console.log(`[${source}]`, message, properties ?? '')
  }

  getAppInsights()?.trackTrace({
    message: `[${source}] ${message}`,
    severityLevel: 1, // Information
    properties,
  })
}
